/** Click a value, see it on the bill - P15 item 1.
 *
 * A value on the record page that was read from a document is a link to the place it was read from:
 * the Document tab opens with that span highlighted and kept highlighted. Three parts, one source of
 * truth each:
 *
 * - `useSourceFields` reads what the server already says (`GET /evidence/{id}/text` for *where*, and
 *   `GET /evidence/{id}/review` for *what it was read as and how sure*) and answers one question per
 *   value: which field, if any, can this value be pointed at. **A value with no confirmed position is
 *   plain text**, never a control that predictably does nothing (design rule 6).
 * - `SourceValue` is the link. It is a button, because it changes what is shown on the page and
 *   navigates nowhere.
 * - `ExtractedValues` is the grid of fields on the Document tab: the row a click on a highlight lands
 *   on. The highlight and the row are the same selection, held by the caller, so the page cannot show
 *   one field highlighted and another selected.
 *
 * **Nothing here finds a position.** The pane draws what `spans.resolve` re-checked on the server;
 * this module never searches the text for a value (ADR-0045: relocation is a second answer wearing
 * the first one's clothes). Pixel-accurate highlights on the original page stay out of scope (register
 * row T-06).
 */

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

import { FieldGrid } from "../components/ui";
import { drawn, fieldLabel } from "./fields";
import type { SourceFields, ValueKind } from "./useSourceFields";
import own from "./recordSource.module.css";

/** A value read from the bill. A button when a position is confirmed, plain text when it is not. */
export function SourceValue({
  source,
  of,
  children,
}: {
  source: SourceFields;
  of: ValueKind;
  children: ReactNode;
}) {
  const field = source.fieldFor(of);
  if (!field) return <>{children}</>;
  return (
    <button
      type="button"
      className={own.link}
      data-testid={`source-link-${of}`}
      data-field={field}
      title="Show on the bill"
      aria-label={`Show ${fieldLabel(field).toLowerCase()} on the bill`}
      onClick={() => source.show(field)}
    >
      {children}
    </button>
  );
}

/** The grid of fields read from the document. A row is the thing a click on a highlight lands on. */
export function ExtractedValues({
  source,
  scrollTo,
}: {
  source: SourceFields;
  /** Bumped by the page when a highlight is pressed, so the row is brought into view even when the
   * same field is pressed twice. */
  scrollTo: { field: string; n: number } | null;
}) {
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!scrollTo) return;
    // `?.` because jsdom has no scrollIntoView; a browser always does.
    root.current
      ?.querySelector(`[data-field-row="${scrollTo.field}"]`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [scrollTo]);

  const spans = source.text?.spans ?? [];
  if (spans.length === 0) return null;
  return (
    <section className="card" ref={root} data-testid="extracted-values">
      <FieldGrid
        sections={[
          {
            title: "Read from the document",
            fields: spans.map((span) => {
              const detail = source.details[span.field];
              const position = drawn(span);
              const here = source.focus === span.field;
              return {
                label: fieldLabel(span.field),
                testId: `extracted-row-${span.field}`,
                value: (
                  <span
                    className={here ? own.rowHere : own.row}
                    data-field-row={span.field}
                    data-selected={here ? "true" : undefined}
                  >
                    {detail?.value ? (
                      position ? (
                        <button
                          type="button"
                          className={own.link}
                          title="Show on the bill"
                          onClick={() => source.show(here ? null : span.field)}
                        >
                          {detail.value}
                        </button>
                      ) : (
                        detail.value
                      )
                    ) : (
                      "—"
                    )}
                    {detail?.confidence ? <small className={own.sure}> {detail.confidence}</small> : null}
                  </span>
                ),
              };
            }),
          },
        ]}
      />
      {source.reviewFailed ? (
        <p className={own.failed} role="status" data-testid="extracted-review-failed">
          What each value was read as could not be loaded.
        </p>
      ) : null}
    </section>
  );
}
